import { ScenarioSummary } from '../domain/types.js';
import { formatPercent, formatRub, formatSignedRub, formatTerm } from './format.js';
import { el } from './dom.js';
import { DiffTone, tone } from './summary.js';

export type CompareEntry = {
  summary: ScenarioSummary;
  /** The scenario in the form. */
  isCurrent: boolean;
  /** `state.baselineId`. */
  isBaseline: boolean;
  /** CSS colour expression shared by the table dot and the chart line. */
  color: string;
};

export type CompareInput = {
  currentId: string;
  baselineId?: string;
  comparisonIds: string[];
  summaries: ScenarioSummary[];
};

/** Colour variable for the N-th (0-based) entry that is neither current nor baseline. */
function extraColor(index: number): string {
  return `var(--series-extra-${(index % 5) + 1})`;
}

/**
 * Ordered comparison set. The first entry is the reference every delta is measured
 * against: the baseline when it is set, otherwise the scenario in the form. Then the
 * scenario in the form (when it is not the reference), then the explicitly added ids in
 * order. Unknown ids and duplicates are skipped.
 */
export function compareEntries(input: CompareInput): CompareEntry[] {
  const byId = new Map(input.summaries.map(summary => [summary.scenarioId, summary]));
  const baselineId = input.baselineId && byId.has(input.baselineId) ? input.baselineId : undefined;
  const order = [baselineId ?? input.currentId, input.currentId, ...input.comparisonIds];
  const seen = new Set<string>();
  const entries: CompareEntry[] = [];
  let extras = 0;
  order.forEach(id => {
    const summary = byId.get(id);
    if (!summary || seen.has(id)) return;
    seen.add(id);
    const isCurrent = id === input.currentId;
    const isBaseline = id === baselineId;
    const color = isCurrent
      ? 'var(--series-current)'
      : isBaseline
        ? 'var(--series-baseline)'
        : extraColor(extras++);
    entries.push({ summary, isCurrent, isBaseline, color });
  });
  return entries;
}

function signedMonths(delta: number): string {
  if (delta === 0) return '—';
  return `${delta > 0 ? '+' : '−'}${Math.abs(delta)} мес.`;
}

/** Rate difference in percentage points: «+1,2 п.п.». */
function signedPoints(delta: number): string {
  const rounded = Math.round(delta * 100) / 100;
  if (rounded === 0) return '—';
  return `${rounded > 0 ? '+' : '−'}${formatPercent(Math.abs(rounded)).replace('%', 'п.п.')}`;
}

/** «Сценарий 2 · базовый · в форме» — the column header without the colour dot. */
function entryLabel(entry: CompareEntry): string {
  const parts = [entry.summary.name];
  if (entry.isBaseline) parts.push('базовый');
  if (entry.isCurrent) parts.push('в форме');
  return parts.join(' · ');
}

type Metric = {
  label: string;
  /** `null` = unknown for this scenario: the cell shows «—» and takes part in no delta. */
  value: (summary: ScenarioSummary) => number | null;
  format: (value: number) => string;
  formatDelta: (delta: number) => string;
};

// Lower is better for every metric, so `tone()` is used with its default.
const METRICS: Metric[] = [
  { label: 'Переплата', value: s => s.totalInterest, format: formatRub, formatDelta: formatSignedRub },
  { label: 'Срок', value: s => s.actualTermMonths, format: formatTerm, formatDelta: signedMonths },
  { label: 'Максимум в месяц', value: s => s.maxMonthlyPayment, format: formatRub, formatDelta: formatSignedRub },
  { label: 'Всего выплатите', value: s => s.totalPaid, format: formatRub, formatDelta: formatSignedRub },
  { label: 'Эквивалентная ставка', value: s => s.equivalentRateApr, format: formatPercent, formatDelta: signedPoints },
];

function headerCell(entry: CompareEntry): HTMLTableCellElement {
  const dot = el('span', { class: 'series-dot', 'aria-hidden': 'true' });
  dot.style.setProperty('--swatch-color', entry.color);
  return el('th', { scope: 'col' }, [dot, entryLabel(entry)]);
}

function metricCell(metric: Metric, entry: CompareEntry, reference: CompareEntry): HTMLTableCellElement {
  const value = metric.value(entry.summary);
  if (value === null) return el('td', {}, ['—']);
  const cell = el('td', {}, [metric.format(value)]);
  const referenceValue = metric.value(reference.summary);
  if (entry !== reference && referenceValue !== null) {
    const delta = value - referenceValue;
    const deltaTone: DiffTone = tone(delta);
    cell.append(' ', el('span', { class: `diff-value ${deltaTone}` }, [metric.formatDelta(delta)]));
  }
  return cell;
}

/** One column per entry, deltas against the first one; hidden for fewer than two entries. */
export function renderComparisonTable(entries: CompareEntry[]): void {
  const container = document.getElementById('comparison-table-container');
  if (!container) return;
  if (entries.length < 2) {
    container.replaceChildren();
    container.style.display = 'none';
    return;
  }
  const reference = entries[0];
  const title = reference.isBaseline ? 'Сравнение с базовым сценарием' : 'Сравнение со сценарием в форме';
  const table = el('table', { class: 'comparison-table' }, [
    el('thead', {}, [el('tr', {}, [el('th', { scope: 'col' }, ['Показатель']), ...entries.map(headerCell)])]),
    el('tbody', {}, METRICS.map(metric => el('tr', {}, [
      el('td', {}, [metric.label]),
      ...entries.map(entry => metricCell(metric, entry, reference)),
    ]))),
  ]);
  const block = el('div', { class: 'comparison-container' }, [
    el('div', { class: 'comparison-header' }, [el('h3', {}, [title])]),
    table,
  ]);
  container.replaceChildren(block);
  container.style.display = 'block';
}

/** Binds «Создать копию для сравнения» (`#compare-create`). */
export function initCompare(onCreate: () => void): void {
  document.getElementById('compare-create')?.addEventListener('click', onCreate);
}

/**
 * `#compare` shows one of three things: the table (two or more entries), the note that the
 * form holds the baseline and nothing else is compared, or the explainer with the create button.
 */
export function renderCompareEntries(entries: CompareEntry[]): void {
  const hasTable = entries.length >= 2;
  const isBaseline = entries.some(entry => entry.isCurrent && entry.isBaseline);
  const intro = document.getElementById('compare-intro');
  const explainer = document.getElementById('compare-explainer');
  const note = document.getElementById('compare-baseline-note');
  if (intro) intro.hidden = hasTable;
  if (explainer) explainer.hidden = isBaseline;
  if (note) note.hidden = !isBaseline;
  renderComparisonTable(entries);
}
